import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { createUserWithEmailAndPassword, onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { FcGoogle } from 'react-icons/fc';
import { PiShieldCheck, PiWarningCircle } from 'react-icons/pi';
import { auth, db } from '../firebaseConfig';
import '../styles/Auth.css';

const FUNCTIONS_BASE = process.env.REACT_APP_FUNCTIONS_BASE || 'https://us-central1-up-convert.cloudfunctions.net';
const ACCEPT_INVITE_HTTP_URL = `${FUNCTIONS_BASE}/acceptInviteHttp`;

function RepJoin() {
  const { code } = useParams();
  const navigate = useNavigate();

  const [mode, setMode] = useState('signup'); // 'signup' | 'login'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // If user is already logged in, try to accept the invite directly
  useEffect(() => {
    if (!code) return;
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user && !loading) {
        try {
          await acceptInviteForUser(user);
        } catch (_) {
          // User is logged in but can't accept — they'll need to use the form
        }
      }
    });
    return () => unsub();
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  const acceptInviteForUser = async (user) => {
    const idToken = await user.getIdToken();
    const res = await fetch(ACCEPT_INVITE_HTTP_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ code: code.toLowerCase() }),
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.error) {
      throw new Error(json.error || 'Failed to accept invite');
    }

    navigate('/rep/dashboard', { replace: true });
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    setError('');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // Ensure user doc exists
      const userRef = doc(db, 'users', result.user.uid);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        await setDoc(userRef, {
          uid: result.user.uid,
          email: result.user.email,
          createdAt: serverTimestamp(),
        }, { merge: true });
      }

      await acceptInviteForUser(result.user);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);

      // Create user doc
      const userRef = doc(db, 'users', cred.user.uid);
      await setDoc(userRef, {
        uid: cred.user.uid,
        email: cred.user.email,
        createdAt: serverTimestamp(),
      }, { merge: true });

      await acceptInviteForUser(cred.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await acceptInviteForUser(cred.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  // No invite code in URL at all
  if (!code) {
    return (
      <div className="auth-page">
        <Link to="/" className="auth-brand">UpConvert</Link>
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <PiWarningCircle size={48} color="#dc2626" style={{ marginBottom: 12 }} />
          <h1 style={{ fontSize: 24, fontFamily: 'var(--font-serif)' }}>Invalid Invite</h1>
          <p className="subhead" style={{ marginTop: 8 }}>
            This invite link is invalid. Please ask your admin for a new invite.
          </p>
          <Link to="/" className="primary-button" style={{ display: 'inline-block', marginTop: 24, textDecoration: 'none', textAlign: 'center' }}>
            Go to homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <Link to="/" className="auth-brand">UpConvert</Link>

      <div className="auth-card">
        <div className="auth-header">
          <p className="eyebrow">Join your team</p>
          <h1>{mode === 'signup' ? 'Create your rep account' : 'Sign in to join'}</h1>
          <p className="subhead">
            Sign up to access your rep dashboard and connect client emails.
          </p>
        </div>

        <div className="auth-form">
          <button
            type="button"
            className="google-button"
            onClick={handleGoogleAuth}
            disabled={loading}
          >
            <FcGoogle size={20} />
            {mode === 'signup' ? 'Sign up with Google' : 'Sign in with Google'}
          </button>

          <div className="divider">
            <span>or {mode === 'signup' ? 'sign up' : 'sign in'} with email</span>
          </div>

          <form
            onSubmit={mode === 'signup' ? handleEmailSignup : handleEmailLogin}
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            <label className="input-label">
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </label>

            <label className="input-label">
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'signup' ? 'Create a password' : 'Enter your password'}
                required
              />
            </label>

            {mode === 'signup' && (
              <label className="input-label">
                Confirm password
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                />
              </label>
            )}

            {error && <p className="error-text">{error}</p>}

            <button type="submit" className="primary-button" disabled={loading}>
              {loading
                ? (mode === 'signup' ? 'Creating account…' : 'Signing in…')
                : (mode === 'signup' ? 'Join team' : 'Sign in & join')}
            </button>
          </form>

          <p className="auth-footer">
            {mode === 'signup' ? (
              <>Already have an account? <button type="button" onClick={() => { setMode('login'); setError(''); }} style={{ color: '#2563eb', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 'inherit' }}>Sign in</button></>
            ) : (
              <>New here? <button type="button" onClick={() => { setMode('signup'); setError(''); }} style={{ color: '#2563eb', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 'inherit' }}>Create account</button></>
            )}
          </p>

          <div className="auth-trust">
            <PiShieldCheck size={16} color="var(--accent-base)" />
            Enterprise-grade encryption
          </div>
        </div>
      </div>
    </div>
  );
}

export default RepJoin;
